import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Divider,
  Paper,
} from "@mui/material";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningIcon from "@mui/icons-material/Warning";
import DescriptionIcon from "@mui/icons-material/Description";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  light: "#1e7e6c",
  white: "#ffffff",
  gray: "#f8fafc",
  lightGray: "#e2e8f0",
  darkGray: "#1e293b",
  error: "#d32f2f",
  success: "#27ae60",
  info: "#2980b9",
  warning: "#ed6c02",
};

const ConfirmacionImportacionLoteFinanciera = ({
  open,
  onClose,
  importing,
  importResult,
  fileName,
  importProgress = 0,
  llave = null,
  filesCount = 0,
}) => {
  // Estado: Importando
  if (importing) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
        disableEscapeKeyDown
        disableBackdropClick
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={28} sx={{ color: colors.white }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color={colors.white}>
                Subiendo {filesCount} Archivo(s)
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                {llave && `Llave: ${llave}`} - Por favor espere
              </MDTypography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 5, px: 4, textAlign: "center" }}>
          <Box position="relative" display="inline-block" mb={3}>
            <CloudUploadIcon sx={{ fontSize: 38, color: colors.secondary, opacity: 0.8 }} />
          </Box>

          <MDTypography variant="h5" fontWeight="bold" color={colors.darkGray} gutterBottom>
            Subiendo archivos...
          </MDTypography>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              py: 1,
              bgcolor: colors.gray,
              borderRadius: 3,
              mt: 2,
            }}
          >
            <AttachFileIcon sx={{ fontSize: 20, color: colors.secondary }} />
            <MDTypography variant="body1" color="text.secondary">
              {fileName || `${filesCount} archivo(s)`}
            </MDTypography>
          </Box>

          {llave && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                px: 3,
                py: 1,
                bgcolor: `${colors.info}15`,
                borderRadius: 3,
                mt: 1,
              }}
            >
              <VpnKeyIcon sx={{ fontSize: 20, color: colors.info }} />
              <MDTypography variant="body1" fontWeight="medium" color={colors.info}>
                Llave: {llave}
              </MDTypography>
            </Box>
          )}

          <Box sx={{ width: "100%", mt: 4 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <MDTypography variant="caption" color="text.secondary">
                Progreso
              </MDTypography>
              <MDTypography variant="caption" fontWeight="bold" color={colors.secondary}>
                {Math.round(importProgress)}%
              </MDTypography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={importProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: `${colors.light}30`,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.secondary,
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          <MDTypography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {importProgress > 0 && importProgress < 100
              ? `Subiendo archivos para la llave ${llave}... ${Math.round(importProgress)}%`
              : "La subida puede tomar unos segundos"}
          </MDTypography>
        </DialogContent>
      </Dialog>
    );
  }

  // Estado: Éxito
  if (importResult?.success) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.light} 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircleIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color={colors.white}>
                Archivos Subidos Exitosamente
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                {llave && `Llave: ${llave}`} - {filesCount} archivo(s) subido(s) correctamente
              </MDTypography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.white }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: "inline-flex",
                p: 2.5,
                borderRadius: "50%",
                bgcolor: `${colors.success}10`,
                mt: 1,
                mb: 2,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 80, color: colors.success }} />
            </Box>
            <MDTypography variant="h4" fontWeight="bold" color={colors.success} gutterBottom>
              ¡Completado!
            </MDTypography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Paper elevation={0} sx={{ p: 3, bgcolor: colors.gray, borderRadius: 3 }}>
            <MDTypography variant="subtitle1" fontWeight="bold" color={colors.primary} mb={2}>
              Resumen de la carga
            </MDTypography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <AttachFileIcon sx={{ fontSize: 22, color: colors.secondary }} />
                <MDTypography variant="body1" color="text.secondary">
                  Archivos:
                </MDTypography>
              </Box>
              <MDTypography variant="body1" fontWeight="medium" color={colors.darkGray}>
                {filesCount} archivo(s) subido(s)
              </MDTypography>
            </Box>

            {llave && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.5,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <VpnKeyIcon sx={{ fontSize: 22, color: colors.info }} />
                    <MDTypography variant="body1" color="text.secondary">
                      Llave:
                    </MDTypography>
                  </Box>
                  <Box>
                    <MDTypography
                      variant="body1"
                      fontWeight="medium"
                      color={colors.info}
                      sx={{ fontFamily: "monospace" }}
                    >
                      {llave}
                    </MDTypography>
                  </Box>
                </Box>
              </>
            )}

            <Divider sx={{ my: 1.5 }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <MDTypography variant="body1" fontWeight="bold" color={colors.success}>
                Estado:
              </MDTypography>
              <MDTypography variant="body1" fontWeight="bold" color={colors.success}>
                Subido correctamente
              </MDTypography>
            </Box>
          </Paper>

          <Box sx={{ mt: 3, p: 2, bgcolor: "#e8f5e9", borderRadius: 3, textAlign: "center" }}>
            <MDTypography variant="body1" fontWeight="bold" color={colors.success}>
              {filesCount} archivo(s) asociado(s) a la llave {llave}
            </MDTypography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            justifyContent: "center",
            bgcolor: colors.gray,
            borderTop: `1px solid ${colors.lightGray}`,
          }}
        >
          <MDButton
            variant="contained"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: colors.success,
              color: colors.white,
              px: 5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": { bgcolor: colors.light },
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  // Estado: Error
  if (importResult?.success === false) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.error} 0%, #c62828 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <ErrorIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color={colors.white}>
                Error al Subir los Archivos
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                {llave && `Llave: ${llave}`} - No se pudieron subir los archivos
              </MDTypography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.white }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: "inline-flex",
                p: 2.5,
                borderRadius: "50%",
                bgcolor: `${colors.error}10`,
                mt: 1,
                mb: 2,
              }}
            >
              <ErrorIcon sx={{ fontSize: 80, color: colors.error }} />
            </Box>
            <MDTypography variant="h4" fontWeight="bold" color={colors.error} gutterBottom>
              ¡Algo salió mal!
            </MDTypography>

            <Paper
              elevation={0}
              sx={{
                bgcolor: "#ffebee",
                borderRadius: 3,
                p: 2.5,
                mt: 2,
                border: `1px solid #ffcdd2`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <WarningIcon sx={{ fontSize: 22, color: colors.error }} />
                <MDTypography variant="body1" fontWeight="medium" color={colors.error}>
                  {importResult.error || "Error al subir los archivos"}
                </MDTypography>
              </Box>
            </Paper>

            {llave && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: `${colors.info}10`,
                  borderRadius: 3,
                }}
              >
                <MDTypography variant="body2" color="text.secondary">
                  Llave: <strong style={{ fontFamily: "monospace" }}>{llave}</strong>
                </MDTypography>
              </Box>
            )}
          </Box>

          <Paper elevation={0} sx={{ p: 3, bgcolor: colors.gray, borderRadius: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <MDTypography variant="subtitle1" fontWeight="bold" color={colors.primary}>
                Posibles soluciones:
              </MDTypography>
            </Box>
            <Box component="ul" sx={{ margin: 0, paddingLeft: "1.75rem" }}>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Verifique que los archivos sean de tipo <strong>PDF, JPEG o PNG</strong>
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Asegúrese que cada archivo no supere los <strong>50MB</strong>
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Verifique que los archivos no estén dañados o corruptos
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Confirme que la llave de conciliación sea válida
                </MDTypography>
              </li>
              <li>
                <MDTypography variant="body2" color="text.secondary">
                  Intente nuevamente en unos momentos
                </MDTypography>
              </li>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            justifyContent: "center",
            bgcolor: colors.gray,
            borderTop: `1px solid ${colors.lightGray}`,
          }}
        >
          <MDButton
            variant="contained"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: colors.error,
              color: colors.white,
              px: 5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": { bgcolor: "#c62828" },
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

ConfirmacionImportacionLoteFinanciera.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  importing: PropTypes.bool,
  importResult: PropTypes.shape({
    success: PropTypes.bool,
    message: PropTypes.string,
    error: PropTypes.string,
    importedCount: PropTypes.number,
    validationSummary: PropTypes.shape({
      totalRows: PropTypes.number,
      validRows: PropTypes.number,
      invalidRows: PropTypes.number,
      duplicateInFile: PropTypes.number,
      duplicateInDatabase: PropTypes.number,
    }),
  }),
  fileName: PropTypes.string,
  importProgress: PropTypes.number,
  llave: PropTypes.string,
  filesCount: PropTypes.number,
};

ConfirmacionImportacionLoteFinanciera.defaultProps = {
  importing: false,
  importResult: null,
  fileName: "",
  importProgress: 0,
  llave: null,
  filesCount: 0,
};

export default ConfirmacionImportacionLoteFinanciera;
